<template>
  <div class="absolute top-0 right-0 z-50" v-if="!language.restricted">
    <div class="relative mr-5">
      <button
        title="Change language"
        @click="isOpen = !isOpen"
        class="relative z-10 flex items-center justify-center px-3 py-1 overflow-hidden bg-white rounded-sm rounded-t-none focus:outline-none md:bg-gray-300"
      >
          <gb-flag :code="language.flagCode" iconPath="/public/flags/" size="mini" class="mr-2"/>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="chevron"
          :class="{ rotate: isOpen }"
          width="14"
          height="10"
          role="presentation"
        >
          <path
            d="M9.211364 7.59931l4.48338-4.867229c.407008-.441854.407008-1.158247 0-1.60046l-.73712-.80023c-.407008-.441854-1.066904-.441854-1.474243 0L7 5.198617 2.51662.33139c-.407008-.441853-1.066904-.441853-1.474243 0l-.737121.80023c-.407008.441854-.407008 1.158248 0 1.600461l4.48338 4.867228L7 10l2.211364-2.40069z"
          ></path>
        </svg>
      </button>
      <div
        v-if="isOpen"
        class="absolute right-0 z-50 w-40 py-2 mt-1 bg-white rounded-sm shadow-xl md:py-1 md:bg-gray-300"
      >
        <button v-for="(language, index) in sortedLanguages" :key="index" class="flex items-center w-full px-3 py-2 text-left md:py-1 hover:bg-darkblue hover:text-white md:px-3" @click="changeLanguage(language.locale, language.flagCode)">
          <gb-flag :code="language.flagCode" iconPath="/public/flags/" size="mini" class="mr-2"/>
          <span>{{ language.name }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState, mapActions } from 'vuex';
import { updateUrlParams } from '@/util/url-params';

export default {
  data: () => ({
    isOpen: false,
    languages: [
      {
        flagCode: 'de',
        locale: 'de',
        name: 'Deutsch'
      },
      {
        flagCode: 'gb',
        locale: 'en',
        name: 'English'
      },
      {
        flagCode: 'pl',
        locale: 'pl',
        name: 'Polski'
      },
      {
        flagCode: 'ru',
        locale: 'ru',
        name: 'русский'
      },
      {
        flagCode: 'ro',
        locale: 'ro',
        name: 'Română'
      },
      {
        flagCode: 'cn',
        locale: 'zh-CN',
        name: '中文'
      },
      {
        flagCode: 'es',
        locale: 'es',
        name: 'Español'
      },
      {
        flagCode: 'pt',
        locale: 'pt-PT',
        name: 'Português'
      },
      {
        flagCode: 'cz',
        locale: 'cs-CZ',
        name: 'Česky'
      },
      {
        flagCode: 'se',
        locale: 'sv-SE',
        name: 'Svenska'
      },
      {
        flagCode: 'no',
        locale: 'no-NO',
        name: 'Norsk'
      },
      {
        flagCode: 'fi',
        locale: 'fi-FI',
        name: 'Finsk'
      },
      {
        flagCode: 'dk',
        locale: 'da-DK',
        name: 'Dansk'
      },
      {
        flagCode: 'fr',
        locale: 'fr-FR',
        name: 'Français'
      },
      {
        flagCode: 'it',
        locale: 'it-IT',
        name: 'Italiano'
      },
      {
        flagCode: 'nl',
        locale: 'nl-NL',
        name: 'Nederlands'
      },
      {
        flagCode: 'sa',
        locale: 'ar',
        name: 'العربية'
      }
    ]
  }),
  created () {
    const handleEscape = e => {
      if (e.key === 'Esc' || e.key === 'Escape') {
        this.isOpen = false;
      }
    };

    document.addEventListener('keydown', handleEscape);

    this.$once('hook:beforeDestroy', () => {
      document.removeEventListener('keydown', handleEscape);
    });

    this.setStateByBrowserLocale();
  },
  methods: {
    ...mapActions(['GET_VEHICLES', 'GET_RESULTS']),
    ...mapMutations(['SET_LANGUAGE', 'UNSET_SELECTED', 'UPDATE_URL_PARAMS']),
    setStateByBrowserLocale () {
      const URLparams = new URLSearchParams(location.search);
      const restricted = URLparams.get('restricted') === 'true';
      const languageActive = this.languages.filter(language => this.$i18n.locale.includes(language.locale));
      this.SET_LANGUAGE({ locale: languageActive[0].locale, flagCode: languageActive[0].flagCode, restricted });
      updateUrlParams('lang', languageActive[0].locale);
    },
    async changeLanguage (locale, flagCode) {
      this.SET_LANGUAGE({ locale: locale, flagCode: flagCode });
      await this.UNSET_SELECTED(
        {
          selected: ['vehicle', 'make', 'model'],
          data: ['vehicles', 'makers', 'models', 'types', 'results']
        }
      );
      updateUrlParams('lang', locale);

      this.getResultsByUrl();
      this.isOpen = false;
      this.GET_VEHICLES();
    },
    getResultsByUrl () {
      const URLparams = new URLSearchParams(location.search);
      if (URLparams.get('selectedType')) {
        this.GET_RESULTS(URLparams.get('selectedType'));
      }
    }
  },
  computed: {
    ...mapState({
      language: state => state.language
    }),
    sortedLanguages () {
      return [...this.languages].sort((a, b) => a.name.localeCompare(b.name));
    }
  }
};
</script>

<style lang="postcss">
svg.chevron {
  transform: rotate(0deg);
  transition-duration: 0.3s;
  fill: theme("colors.gray.700");
}
svg.chevron.rotate {
  transform: rotate(180deg);
  transition-duration: 0.3s;
}
</style>
